/**
 * Configuración centralizada del tema de Clerk
 * 
 * Este archivo contiene el tema personalizado para que Clerk coincida
 * con el diseño del sistema Revvly, usando los mismos colores grises
 * y azules que se usan en toda la aplicación.
 */

import { dark } from "@clerk/themes";

// Configuración de variables que coinciden con el sistema Revvly
export const revvlyClerkVariables = {
  // Colores principales que coinciden con el sistema
  colorPrimary: '#3b82f6', // blue-500 - azul principal del sistema
  colorBackground: '#1f2937', // gray-800 - fondo principal de cards
  colorInputBackground: '#374151', // gray-700 - fondo de inputs
  colorForeground: '#ffffff', // texto principal blanco
  colorMutedForeground: '#9ca3af', // gray-400 - texto secundario
  colorBorder: '#4b5563', // gray-600 - bordes
  colorRing: '#3b82f6', // blue-500 - focus ring
  colorSuccess: '#10b981', // green-500 - success states
  colorDanger: '#ef4444', // red-500 - error states
  colorWarning: '#f59e0b', // amber-500 - warning states
  colorNeutral: '#6b7280', // gray-500 - neutral states
  colorMuted: '#374151', // gray-700 - muted backgrounds
  colorShadow: 'rgba(0, 0, 0, 0.5)', // sombras
  colorModalBackdrop: 'rgba(0, 0, 0, 0.7)', // backdrop del modal
  fontFamily: 'inherit', // usar la fuente del sistema
  borderRadius: '0.38rem', // rounded-lg - 8px border radius
  fontSize: '0.875rem', // text-sm - 14px como base
  spacing: '1rem' // espaciado base de 16px
};

// Configuración de elementos específicos
export const revvlyClerkElements = {
  // Personalizar elementos específicos para que coincidan con el sistema
  card: 'bg-gray-800 rounded-lg',
  headerTitle: 'text-xl font-bold text-white',
  headerSubtitle: 'text-sm text-gray-400',
  formButtonPrimary: 'bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors duration-200',
  
  // Botones de proveedores sociales (Google, etc.)
  socialButtonsBlockButton: 'bg-gray-700 hover:bg-gray-600 border border-gray-600 text-white font-medium py-2.5 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2',
  socialButtonsBlockButtonText: 'text-white font-medium text-sm',
  socialButtonsProviderIcon: 'w-5 h-5',
  
  // Inputs y campos de formulario
  formFieldInput: 'bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-lg p-3 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors',
  formFieldLabel: 'text-sm font-medium text-gray-300 mb-2',
  
  // Textos y enlaces
  dividerLine: 'border-gray-600',
  dividerText: 'text-gray-400 text-sm',
  footerActionLink: 'text-blue-400 hover:text-blue-300 transition-colors',
  identityPreviewText: 'text-gray-300',
  identityPreviewEditButton: 'text-blue-400 hover:text-blue-300',
  formResendCodeLink: 'text-blue-400 hover:text-blue-300',
  
  // Campos especiales
  otpCodeFieldInput: 'bg-gray-700 border border-gray-600 text-white text-center font-mono rounded-lg',
  
  // Estados de alerta y mensajes
  alertText: 'text-sm',
  formFieldSuccessText: 'text-green-400 text-sm',
  formFieldErrorText: 'text-red-400 text-sm',
  formFieldWarningText: 'text-amber-400 text-sm',
  
  // Modal
  modalBackdrop: 'backdrop-blur-sm bg-black/70',
  modalContent: 'bg-gray-800 rounded-lg',
  
  // Asegurar que todos los elementos tengan bordes redondeados
  formFieldInputShowPasswordButton: 'text-gray-400 hover:text-white rounded',
  formFieldAction: 'text-blue-400 hover:text-blue-300 text-sm rounded',
  
  // Contenedores principales
  rootBox: 'bg-gray-800 rounded-lg',
  main: 'bg-gray-800 rounded-lg',
  
  // UserButton dropdown
  userButtonPopoverCard: 'bg-gray-800 border border-gray-600 rounded-lg shadow-xl',
  userButtonPopoverActionButton: 'text-gray-50 hover:bg-gray-700 hover:text-white transition-colors duration-200',
  userButtonPopoverActionButtonText: 'text-gray-50 font-medium text-sm',
  userButtonPopoverActionButtonIcon: 'text-gray-300',
  userButtonPopoverFooter: 'border-t border-gray-600'
};

// Configuración completa del tema para ClerkProvider
export const revvlyClerkTheme = {
  baseTheme: dark,
  variables: revvlyClerkVariables,
  elements: revvlyClerkElements
};

// Configuración para modales individuales (como openUserProfile)
export const revvlyClerkModalTheme = {
  baseTheme: 'dark',
  variables: revvlyClerkVariables,
  elements: revvlyClerkElements
};

// Configuración específica para diferentes componentes si se necesita
export const revvlyClerkComponentThemes = {
  signIn: revvlyClerkTheme,
  signUp: revvlyClerkTheme,
  userProfile: revvlyClerkModalTheme,
  userButton: revvlyClerkTheme,
  organizationProfile: revvlyClerkModalTheme,
  organizationSwitcher: revvlyClerkTheme
};

export default revvlyClerkTheme;